import { TiltParallax } from "./game/tilt-parallax";
import type { Point } from "./game/geometry";

export type BackgroundMotionStatus =
  | "unsupported"
  | "permission-required"
  | "requesting"
  | "waiting"
  | "active"
  | "disabled"
  | "denied"
  | "unavailable"
  | "reduced-motion";

export class BackgroundMotion {
  private readonly tilt = new TiltParallax();
  private readonly reduced = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  );
  private status: BackgroundMotionStatus = "unsupported";
  private listening = false;
  private disposed = false;
  private disabled = false;
  private generation = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    private readonly onOffset: (point: Point) => void,
    private readonly onStatus: (status: BackgroundMotionStatus) => void,
  ) {}

  start(): void {
    document.addEventListener("visibilitychange", this.visibility);
    window.addEventListener("pagehide", this.reset);
    window.addEventListener("orientationchange", this.reset);
    screen.orientation?.addEventListener("change", this.reset);
    this.reduced.addEventListener("change", this.preference);
    this.prepare();
  }

  async enable(): Promise<void> {
    if (this.disposed || this.status === "requesting") return;
    this.disabled = false;
    if (!this.supported() || this.reduced.matches) {
      this.prepare();
      return;
    }
    const generation = ++this.generation;
    const orientation: typeof DeviceOrientationEvent & {
      requestPermission?: () => Promise<PermissionState>;
    } = DeviceOrientationEvent;
    if (orientation.requestPermission) {
      this.setStatus("requesting");
      try {
        const permission = await orientation.requestPermission();
        if (this.disposed || generation !== this.generation) return;
        if (permission !== "granted") {
          this.setStatus("denied");
          return;
        }
      } catch {
        if (this.disposed || generation !== this.generation) return;
        console.warn("Background motion permission failed", {
          feature: "deviceorientation",
        });
        this.setStatus("denied");
        return;
      }
    }
    if (this.reduced.matches) {
      this.prepare();
      return;
    }
    this.listen();
  }

  disable(): void {
    this.disabled = true;
    this.generation++;
    this.stop();
    this.setStatus("disabled");
  }

  destroy(): void {
    this.disposed = true;
    this.generation++;
    this.stop();
    document.removeEventListener("visibilitychange", this.visibility);
    window.removeEventListener("pagehide", this.reset);
    window.removeEventListener("orientationchange", this.reset);
    screen.orientation?.removeEventListener("change", this.reset);
    this.reduced.removeEventListener("change", this.preference);
  }

  private supported(): boolean {
    return (
      window.isSecureContext &&
      navigator.maxTouchPoints > 0 &&
      typeof DeviceOrientationEvent !== "undefined"
    );
  }

  private prepare(): void {
    if (!this.supported()) {
      this.setStatus("unsupported");
      return;
    }
    if (this.reduced.matches) {
      this.stop();
      this.setStatus("reduced-motion");
      return;
    }
    if (this.disabled) {
      this.setStatus("disabled");
      return;
    }
    if (this.listening) return;
    const orientation: typeof DeviceOrientationEvent & {
      requestPermission?: () => Promise<PermissionState>;
    } = DeviceOrientationEvent;
    if (orientation.requestPermission) this.setStatus("permission-required");
    else this.listen();
  }

  private listen(): void {
    if (this.listening) return;
    this.listening = true;
    this.reset();
    this.setStatus("waiting");
    window.addEventListener("deviceorientation", this.orientation, {
      passive: true,
    });
    this.timer = setTimeout(() => {
      this.stop();
      this.setStatus("unavailable");
    }, 5000);
  }

  private stop(): void {
    window.removeEventListener("deviceorientation", this.orientation);
    clearTimeout(this.timer);
    this.timer = undefined;
    this.listening = false;
    this.reset();
  }

  private setStatus(status: BackgroundMotionStatus): void {
    this.status = status;
    if (!this.disposed) this.onStatus(status);
  }

  private reset = (): void => {
    this.tilt.reset();
    this.onOffset({ x: 0, y: 0 });
  };
  private visibility = (): void => {
    this.reset();
  };
  private preference = (): void => {
    this.generation++;
    this.prepare();
  };
  private orientation = (event: DeviceOrientationEvent): void => {
    if (document.hidden || this.reduced.matches) return;
    if (
      event.beta === null ||
      event.gamma === null ||
      !Number.isFinite(event.beta) ||
      !Number.isFinite(event.gamma)
    ) {
      this.reset();
      return;
    }
    clearTimeout(this.timer);
    this.timer = undefined;
    if (this.status !== "active") this.setStatus("active");
    this.onOffset(
      this.tilt.sample(event.beta, event.gamma, screen.orientation?.angle ?? 0),
    );
  };
}
